import { useMutation } from '@tanstack/react-query';
import {
  setHomepagePortalApi,
  unsetHomepagePortalApi,
} from '../helpers/api/portalApi';
import { handleMutationSuccess } from './helpers';
import {
  guideWriteCommandApi,
  sendBatchCommandApi,
} from '../helpers/api/guidelinesApi';
import { notify } from '@bynder/design-system';
import {
  HomepageSetNotification,
  HomepageUnsetNotification,
} from '../types/notifications';
import { getCommand } from '../helpers/api/commands';
import { useGetCurrentHomepage } from '../queries/homepage';
import { AxiosResponse } from 'axios';
import { generateUUID } from '../helpers/utils';
import { useTranslate } from '@bynder/localization';
import { EntityVisibility, GuideType } from '../types';
import { getEmptyLegacyModificationGroup } from '../helpers/permissions';
import { useModalStore } from '../stores/modalStore';
import { OVERVIEW_KEYS } from '../queries/guides';
import { useAssignThemesetToGuide } from './themesets';
import { TemplatesEnum } from '../constants/templates/constants';
import { templateBuilder } from '../constants/templates/templateBuilder';
import { EntityEnum } from '../components/modals/NewCreateModal';

export const useHomepageMutations = () => {
  const { data: currentHomepage } = useGetCurrentHomepage();
  const { translate } = useTranslate();
  const newHomepageId = generateUUID();
  const newPageId = generateUUID();
  const newHomepageVersion = 0;
  const toggleIsCreatingLandingPage =
    useModalStore.use.toggleIsCreatingLandingPage();
  const closeModal = useModalStore.use.closeModal();
  const assignThemesetToGuideMutation = useAssignThemesetToGuide();

  const createBlankHomepage = useMutation(
    (createGuideOptions: { guideTitle: string; themesetId: string }) => {
      const { createGuideWithTemplate, editGuideAccess } = getCommand(
        newHomepageId,
        0,
      );

      const createBlankHomepageCommand = createGuideWithTemplate({
        guideTitle: createGuideOptions.guideTitle,
        chapterId: generateUUID(),
        chapterTitle: translate('GUIDE.UNTITLED_CHAPTER'),
        pageId: generateUUID(),
        pageTitle: translate('GUIDE.UNTITLED_PAGE'),
        sectionId: generateUUID(),
        guideType: GuideType.HOME_PAGE,
      });

      const setVisibilityPortal = editGuideAccess({
        viewAccessChanges: getEmptyLegacyModificationGroup(),
        editAccessChanges: getEmptyLegacyModificationGroup(),
        guideVisibility: EntityVisibility.PORTAL,
      });

      return sendBatchCommandApi(
        newHomepageId,
        [createBlankHomepageCommand, setVisibilityPortal],
        newHomepageVersion,
      );
    },
    {
      onMutate: () => {
        toggleIsCreatingLandingPage(true);
        closeModal();
      },
      onSuccess: (data, variables) => {
        if (variables.themesetId) {
          assignThemesetToGuideMutation.mutate({
            guideId: newHomepageId,
            themesetId: variables.themesetId,
          });
        }
        handleMutationSuccess('Homepage Created', data, {
          entityId: newHomepageId,
          queryPath: OVERVIEW_KEYS.OVERVIEW_GUIDES,
          payload: {
            isCreatingLandingPage: true,
            newPageId,
          },
        });
      },
    },
  );

  const createHomepageFromTemplate = useMutation(
    (mutationOptions: {
      pageTitle: string;
      pageTemplate: TemplatesEnum;
      themesetId: string;
    }) => {
      const { editGuideAccess } = getCommand(newHomepageId, 0);

      const guideTemplate = templateBuilder({
        guideId: newHomepageId,
        chapterId: generateUUID(),
        pageId: newPageId,
        sectionId: generateUUID(),
        guideVersion: newHomepageVersion,
        template: mutationOptions.pageTemplate,
        guideTitle: mutationOptions.pageTitle,
        groupId: undefined,
        entityType: EntityEnum.HOMEPAGE,
      });

      const setVisibilityPortal = editGuideAccess({
        viewAccessChanges: getEmptyLegacyModificationGroup(),
        editAccessChanges: getEmptyLegacyModificationGroup(),
        guideVisibility: EntityVisibility.PORTAL,
      });

      return sendBatchCommandApi(
        newHomepageId,
        [...(guideTemplate || []), ...[setVisibilityPortal]],
        newHomepageVersion,
      );
    },

    {
      onMutate: () => {
        toggleIsCreatingLandingPage(true);
        closeModal();
      },
      onSuccess: (data, variables) => {
        if (variables.themesetId) {
          assignThemesetToGuideMutation.mutate({
            guideId: newHomepageId,
            themesetId: variables.themesetId,
          });
        }
        handleMutationSuccess('Homepage Created', data, {
          entityId: newHomepageId,
          queryPath: OVERVIEW_KEYS.OVERVIEW_GUIDES,
          payload: {
            isCreatingLandingPage: true,
            newPageId,
          },
        });
      },
    },
  );

  const publishHomepage = useMutation(
    (mutationOptions: { guideId: string; guideVersion: number }) => {
      const { publishHomepage } = getCommand(
        mutationOptions.guideId,
        mutationOptions.guideVersion,
      );

      return guideWriteCommandApi(
        publishHomepage({ previousHomepageId: currentHomepage }),
      );
    },
    {
      onSuccess: (data, variables) => {
        handleMutationSuccess('Landing page set as homepage', data, {
          entityId: variables.guideId,
        });
        notify(HomepageSetNotification);
      },
    },
  );

  const setHomepage = useMutation(
    (mutationOptions: { guideId: string; guideVersion: number }) =>
      setHomepagePortalApi(mutationOptions.guideId),
    {
      onSuccess: (_, variables) => {
        publishHomepage.mutate({
          guideId: variables.guideId,
          guideVersion: variables.guideVersion,
        });
      },
    },
  );

  const unsetHomepage = useMutation(
    (options: { guideId: string; guideVersion: number }) => {
      if (currentHomepage === options.guideId) {
        return unsetHomepagePortalApi();
      }
      return Promise.resolve() as unknown as Promise<AxiosResponse>;
    },
    {
      onSuccess: (_, variables) => {
        unpublishHomepage.mutate({
          guideId: variables.guideId,
          guideVersion: variables.guideVersion,
        });
      },
    },
  );

  const unpublishHomepage = useMutation(
    (mutationOptions: { guideId: string; guideVersion: number }) => {
      const unpublishGuideCommand = getCommand(
        mutationOptions.guideId,
        mutationOptions.guideVersion,
      ).unpublishGuide;

      return guideWriteCommandApi(unpublishGuideCommand());
    },
    {
      onSuccess: (data, variables) => {
        handleMutationSuccess('Landing page unset as homepage', data, {
          entityId: variables.guideId,
        });
      },
      onMutate: () => {
        notify(HomepageUnsetNotification);
      },
    },
  );

  return {
    createBlankHomepage,
    createHomepageFromTemplate,
    setHomepage,
    unsetHomepage,
  };
};
